import { useState, useEffect, useCallback } from 'react'
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button, Stack,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert,
  FormControlLabel, Switch, Divider, Grid, IconButton, Tooltip,
} from '@mui/material'
import { Refresh, Add, Event, CheckCircle, DeleteOutline } from '@mui/icons-material'
import { demoApi } from '../services/api'

/**
 * Demos requested, scheduled and completed.
 *
 * Platform-only. Every route this calls is gated on is_platform_admin
 * server-side — a tenant's own admin gets 403 — so nothing here is reachable
 * from a tenant surface even if the component were rendered by mistake.
 *
 * Web submissions and demos the team logged by hand share one list, because
 * the question being asked is "what demos are in flight", which spans both.
 */

const STATUSES = ['requested', 'scheduled', 'completed', 'cancelled']

const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  requested: 'warning',
  scheduled: 'info',
  completed: 'success',
  cancelled: 'error',
}

const ADOPTION_LABEL: Record<string, string> = {
  exploring: 'Building first agents', piloting: 'Piloting with users',
  production: 'In production, still building', scaling: 'Teams shipping continuously',
}

const CADENCE_LABEL: Record<string, string> = {
  weekly: 'Weekly or faster', monthly: 'Monthly',
  quarterly: 'Quarterly', ad_hoc: 'No set rhythm', unsure: 'Not sure',
}

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

export default function DemoRequestsPanel() {
  const [rows, setRows] = useState<any[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [includeDrafts, setIncludeDrafts] = useState(false)
  const [selected, setSelected] = useState<any>(null)
  const [logging, setLogging] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<any>(null)

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await demoApi.list({
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        include_drafts: includeDrafts,
        limit: 200,
      })
      setRows(res.data?.entries || [])
      setCounts(res.data?.status_counts || {})
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'Platform administrator access required.'
        : 'Failed to load demo requests')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, sourceFilter, includeDrafts])

  useEffect(() => { fetchRows() }, [fetchRows])

  // Irreversible and it removes someone's contact details, so it asks first.
  const remove = async (record: any) => {
    try {
      await demoApi.remove(record.id)
      setConfirmDelete(null)
      if (selected?.id === record.id) setSelected(null)
      await fetchRows()
    } catch {
      setError('Delete failed')
    }
  }

  const patch = async (id: string, data: any) => {
    try {
      await demoApi.update(id, data)
      await fetchRows()
      if (selected?.id === id) {
        const fresh = await demoApi.get(id)
        setSelected(fresh.data)
      }
    } catch {
      setError('Update failed')
    }
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {['requested', 'scheduled', 'completed', 'draft'].map(s => (
          <Grid item xs={6} sm={3} key={s}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color={s === 'draft' ? 'text.secondary' : 'primary'}>
                  {counts[s] || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary"
                            sx={{ textTransform: 'capitalize' }}>
                  {s === 'draft' ? 'Started, not submitted' : s}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Source</InputLabel>
          <Select label="Source" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="web">Website</MenuItem>
            <MenuItem value="admin">Logged by team</MenuItem>
          </Select>
        </FormControl>
        {/* A started-then-abandoned form is a signal, not a task — off by
            default so it does not crowd the work queue. */}
        <FormControlLabel
          control={<Switch checked={includeDrafts} size="small"
                           onChange={e => setIncludeDrafts(e.target.checked)} />}
          label="Include unsubmitted"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<Add />} variant="contained" onClick={() => setLogging(true)}>
          Log a demo
        </Button>
        <Button startIcon={<Refresh />} onClick={fetchRows}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Card><CardContent>
          <Typography color="text.secondary">
            No demo requests yet. Submissions from the Request Demo form appear here,
            alongside demos you log by hand.
          </Typography>
        </CardContent></Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Stack</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Cadence</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                          onClick={() => setSelected(r)}>
                  <TableCell>{r.company || <em>unknown</em>}</TableCell>
                  <TableCell>
                    {r.name || '—'}
                    {r.email && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {r.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(r.frameworks || []).length
                      ? (r.frameworks || []).slice(0, 3).join(', ') +
                        ((r.frameworks || []).length > 3 ? ` +${r.frameworks.length - 3}` : '')
                      : '—'}
                  </TableCell>
                  <TableCell>{ADOPTION_LABEL[r.adoption_stage] || '—'}</TableCell>
                  <TableCell>{CADENCE_LABEL[r.release_cadence] || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'}
                          sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined"
                          label={r.source === 'admin' ? 'Team' : 'Website'} />
                  </TableCell>
                  <TableCell>{fmt(r.submitted_at || r.created_at)}</TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    {r.status === 'requested' && (
                      <Button size="small" startIcon={<Event />}
                              onClick={() => patch(r.id, { status: 'scheduled' })}>
                        Schedule
                      </Button>
                    )}
                    {r.status === 'scheduled' && (
                      <Button size="small" startIcon={<CheckCircle />}
                              onClick={() => patch(r.id, { status: 'completed' })}>
                        Complete
                      </Button>
                    )}
                    <Tooltip title="Delete permanently">
                      <IconButton size="small" onClick={() => setConfirmDelete(r)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DemoDetailDialog
        record={selected}
        onClose={() => setSelected(null)}
        onPatch={patch}
      />
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this record?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmDelete?.company || confirmDelete?.email || 'This record'} will be
            removed permanently, along with any contact details it holds. This cannot
            be undone.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            To record that a demo is not going ahead, set its status to cancelled
            instead — that keeps the history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => remove(confirmDelete)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <LogDemoDialog
        open={logging}
        onClose={() => setLogging(false)}
        onSaved={() => { setLogging(false); fetchRows() }}
      />
    </Box>
  )
}


function DemoDetailDialog({ record, onClose, onPatch }: any) {
  const [internalNotes, setInternalNotes] = useState('')
  const [outcome, setOutcome] = useState('')

  useEffect(() => {
    setInternalNotes(record?.internal_notes || '')
    setOutcome(record?.outcome || '')
  }, [record?.id])

  if (!record) return null

  const field = (label: string, value: any) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">
        {Array.isArray(value) ? (value.length ? value.join(', ') : '—') : (value || '—')}
      </Typography>
    </Box>
  )

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{record.company || 'Demo request'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Contact</Typography>
            {field('Name', record.name)}
            {field('Email', record.email)}
            {field('Job title', record.job_title)}
            {field('Phone', record.phone)}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Environment</Typography>
            {field('Frameworks', record.frameworks)}
            {field('Adoption stage', ADOPTION_LABEL[record.adoption_stage] || record.adoption_stage)}
            {field('Hosting', record.hosting)}
            {field('Use cases / agents',
                   record.use_case_count_band || record.agent_count_band
                     ? `${record.use_case_count_band || '?'} use cases · ${record.agent_count_band || '?'} agents`
                     : null)}
            {field('Change cadence', CADENCE_LABEL[record.release_cadence] || record.release_cadence)}
            {field('Interested in', record.interested_features)}
          </Grid>
          <Grid item xs={12}>
            {field('What they asked to see', record.notes)}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Internal</Typography>
        <Stack spacing={2}>
          <TextField label="Internal notes" multiline rows={3} size="small" fullWidth
                     value={internalNotes} onChange={e => setInternalNotes(e.target.value)} />
          <TextField label="Outcome" multiline rows={2} size="small" fullWidth
                     value={outcome} onChange={e => setOutcome(e.target.value)} />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {STATUSES.map(s => (
            <Button key={s} size="small"
                    variant={record.status === s ? 'contained' : 'outlined'}
                    onClick={() => onPatch(record.id, { status: s })}
                    sx={{ textTransform: 'capitalize' }}>
              {s}
            </Button>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Scheduled {fmt(record.scheduled_at)} · Completed {fmt(record.completed_at)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained"
                onClick={() => onPatch(record.id, { internal_notes: internalNotes, outcome })}>
          Save notes
        </Button>
      </DialogActions>
    </Dialog>
  )
}


function LogDemoDialog({ open, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ company: '', name: '', email: '', status: 'completed' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    if (!form.company.trim()) { setError('Company is required'); return }
    setSaving(true)
    setError('')
    try {
      await demoApi.log(form)
      setForm({ company: '', name: '', email: '', status: 'completed' })
      onSaved()
    } catch {
      setError('Could not save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Log a demo</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For demos that did not come through the website, so the record of what
          the team has shown stays in one place.
        </Typography>
        <Stack spacing={2}>
          <TextField label="Company" required size="small" value={form.company}
                     onChange={e => setForm({ ...form, company: e.target.value })} />
          <TextField label="Contact name" size="small" value={form.name}
                     onChange={e => setForm({ ...form, name: e.target.value })} />
          <TextField label="Contact email" size="small" value={form.email}
                     onChange={e => setForm({ ...form, email: e.target.value })} />
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Outcome" multiline rows={2} size="small" value={form.outcome || ''}
                     onChange={e => setForm({ ...form, outcome: e.target.value })} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
